import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { formatDate, formatTimeAgo } from '@/lib/utils';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { RealtimeTimeline } from '@/components/dashboard/RealtimeTimeline';
import { ChecklistRail } from '@/components/dashboard/ChecklistRail';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft, XCircle, RefreshCw, Mail, Send, Inbox,
  Server, Globe, Clock, Copy, ArrowUpRight, ArrowDownLeft, RotateCcw,
  MessageSquare, Wifi, Zap, Pause, Download
} from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { Input } from '@/components/ui/input';

export function JobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.role === 'admin';
  const [job, setJob] = useState(null);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [usage, setUsage] = useState(null);
  const wsRef = useRef(null);

  const loadJob = useCallback(async () => {
    try {
      const data = await api.getJob(jobId);
      setJob(data);
      
      // Load emails if available
      if (data.emails_sent > 0 || data.emails_received > 0) {
        const emailData = await api.getJobEmails(jobId);
        setEmails(emailData.emails || []);
      }

      // Load chat messages
      try {
        const msgData = await api.getJobMessages(jobId);
        setMessages(msgData.messages || []);
      } catch { /* ignore if endpoint not yet available */ }

      // Load API usage
      try {
        const usageData = await api.getJobUsage(jobId);
        setUsage(usageData);
      } catch { /* ignore */ }
    } catch (err) {
      console.error('Load job error:', err);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  // WebSocket for real-time updates + fallback polling
  useEffect(() => {
    loadJob();

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/ws/jobs/${jobId}`;

    let ws = null;
    let reconnectTimer = null;
    let pingTimer = null;
    let pollTimer = null;
    let destroyed = false;

    function startPolling() {
      if (pollTimer) return;
      pollTimer = setInterval(() => { if (!destroyed) loadJob(); }, 5000);
    }

    function stopPolling() {
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    }

    function connect() {
      if (destroyed) return;
      try {
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          if (destroyed) { ws.close(); return; }
          setWsConnected(true);
          stopPolling();
          pingTimer = setInterval(() => {
            if (ws && ws.readyState === WebSocket.OPEN) ws.send('ping');
          }, 25000);
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'timeline' || data.type === 'status_change') {
              loadJob();
            }
          } catch { /* ignore */ }
        };

        ws.onclose = () => {
          if (destroyed) return;
          setWsConnected(false);
          if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
          startPolling();
          reconnectTimer = setTimeout(connect, 3000);
        };

        ws.onerror = () => {
          if (ws) ws.close();
        };
      } catch {
        setWsConnected(false);
        startPolling();
        reconnectTimer = setTimeout(connect, 3000);
      }
    }

    connect();

    return () => {
      destroyed = true;
      stopPolling();
      if (pingTimer) clearInterval(pingTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) { ws.onclose = null; ws.close(); }
    };
  }, [jobId, loadJob]);

  const handleCancel = async () => {
    if (!window.confirm('Cancel this delisting job?')) return;
    setCancelling(true);
    try {
      await api.cancelJob(jobId);
      loadJob();
    } catch (err) {
      console.error('Cancel error:', err);
    } finally {
      setCancelling(false);
    }
  };

  const handleRestart = async () => {
    if (!window.confirm('Restart this job from scratch? All progress will be deleted.')) return;
    setRestarting(true);
    try {
      await api.restartJob(jobId);
      loadJob();
    } catch (err) {
      console.error('Restart error:', err);
      alert('Failed to restart job: ' + (err.message || 'Unknown error'));
    } finally {
      setRestarting(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || sending) return;
    setSending(true);
    try {
      await api.sendJobMessage(jobId, chatInput.trim());
      setChatInput('');
      loadJob();
    } catch (err) {
      console.error('Send message error:', err);
    } finally {
      setSending(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Job not found.</p>
        <Button variant="outline" onClick={() => navigate('/jobs')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Jobs
        </Button>
      </div>
    );
  }

  const isActive = ['researching', 'in_progress', 'waiting_email', 'paused'].includes(job.status);
  const canRestart = ['failed', 'cancelled', 'researching', 'in_progress', 'paused', 'success'].includes(job.status);

  return (
    <div data-testid="job-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => navigate('/jobs')} data-testid="btn-back" className="flex-shrink-0">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg sm:text-xl font-semibold font-mono truncate">{job.ip}</h2>
              <StatusBadge status={job.status} size="md" pulse />
              {isActive && (
                wsConnected ? (
                  <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-green-400 border-green-400/30" data-testid="ws-badge">
                    <Wifi className="w-2.5 h-2.5 mr-1" /> Live
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-muted-foreground" data-testid="ws-badge">
                    <RefreshCw className="w-2.5 h-2.5 mr-1 animate-spin" /> Auto-refresh
                  </Badge>
                )
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5 truncate">
              {job.blacklist_name || job.blacklist}
              <span className="mx-2">&middot;</span>
              <span className="font-mono text-xs">{job.job_id.slice(0, 12)}...</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap pl-10 sm:pl-0">
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                const token = localStorage.getItem('delist_token');
                window.open(`${backendUrl}/api/delist/${job.job_id}/debug-log?token=${token}`, '_blank');
              }}
              data-testid="btn-debug-log"
            >
              <Download className="w-4 h-4 mr-2" />
              Debug Log
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={loadJob} data-testid="btn-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
          {canRestart && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleRestart}
              disabled={restarting}
              data-testid="btn-restart-job"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              {restarting ? 'Restarting...' : 'Restart Job'}
            </Button>
          )}
          {isActive && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleCancel}
              disabled={cancelling}
              data-testid="btn-cancel-job"
            >
              <XCircle className="w-4 h-4 mr-2" /> Cancel
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Paused Banner */}
        {job.status === 'paused' && (
          <div className="lg:col-span-3 p-4 rounded-lg border border-orange-400/30 bg-orange-400/5" data-testid="paused-banner">
            <div className="flex items-center gap-3">
              <Pause className="w-5 h-5 text-orange-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-orange-400">Job pausiert — keine Credits verfügbar</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Dieser Job wird automatisch fortgesetzt, sobald Credits aufgeladen werden.
                </p>
              </div>
            </div>
          </div>
        )}
        {/* Left Column: Timeline */}
        <div className="lg:col-span-2 space-y-6">
          {/* Timeline Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Agent Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RealtimeTimeline entries={job.timeline || []} isAdmin={isAdmin} />
            </CardContent>
          </Card>

          {/* Emails */}
          {emails.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Email Communication
                  <Badge variant="outline" className="ml-2">
                    <Send className="w-3 h-3 mr-1" /> {job.emails_sent} sent
                  </Badge>
                  <Badge variant="outline">
                    <Inbox className="w-3 h-3 mr-1" /> {job.emails_received} received
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {emails.map(email => (
                    <div
                      key={email.id}
                      data-testid={`email-${email.direction}`}
                      className="p-4 rounded-lg border bg-card/50 hover:bg-card transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {email.direction === 'sent' ? (
                            <ArrowUpRight className="w-4 h-4 text-blue-500" />
                          ) : (
                            <ArrowDownLeft className="w-4 h-4 text-green-500" />
                          )}
                          <Badge variant={email.direction === 'sent' ? 'default' : 'secondary'} className="text-xs">
                            {email.direction === 'sent' ? 'Sent' : 'Received'}
                          </Badge>
                          {email.delivery_status && (
                            <Badge variant="outline" className="text-xs">
                              {email.delivery_status}
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatTimeAgo(email.created_at)}
                        </span>
                      </div>

                      <div className="space-y-1 mb-2">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-muted-foreground">From:</span>
                          <span className="font-mono text-foreground">{email.from_addr || '—'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-muted-foreground">To:</span>
                          <span className="font-mono text-foreground">{email.to_addr}</span>
                        </div>
                        {email.subject && (
                          <div className="flex items-start gap-2 text-xs mt-2">
                            <span className="text-muted-foreground shrink-0">Subject:</span>
                            <span className="font-medium">{email.subject}</span>
                          </div>
                        )}
                      </div>

                      {email.body_text && (
                        <div className="mt-3 p-3 rounded bg-muted/30 border">
                          <pre className="text-xs whitespace-pre-wrap break-words font-mono text-muted-foreground">
                            {email.body_text.length > 500 
                              ? email.body_text.substring(0, 500) + '...' 
                              : email.body_text}
                          </pre>
                        </div>
                      )}

                      {email.analysis?.outcome && (
                        <div className="mt-3 flex items-center gap-2">
                          <Badge 
                            variant={
                              email.analysis.outcome === 'success' ? 'default' : 
                              email.analysis.outcome === 'denied' ? 'destructive' : 
                              'secondary'
                            }
                            className="text-xs"
                          >
                            {email.analysis.outcome}
                          </Badge>
                          {email.analysis.action_required && (
                            <span className="text-xs text-muted-foreground">
                              {email.analysis.action_required}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Agent Chat */}
          <Card data-testid="agent-chat">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                <MessageSquare className="w-4 h-4" /> Agent Instructions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {messages.length > 0 && (
                <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                  {messages.map(msg => (
                    <div
                      key={msg.id}
                      data-testid={`chat-msg-${msg.role}`}
                      className={`p-2.5 rounded-lg text-xs ${
                        msg.role === 'user'
                          ? 'bg-primary/10 border border-primary/20 ml-8'
                          : 'bg-muted/50 border border-border mr-8'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-[10px] uppercase tracking-wide">
                          {msg.role === 'user' ? 'You' : 'Agent'}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {formatTimeAgo(msg.created_at)}
                        </span>
                        {!msg.read && msg.role === 'user' && (
                          <Badge variant="outline" className="text-[9px] px-1 py-0">pending</Badge>
                        )}
                      </div>
                      <p className="text-foreground/80 whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  ))}
                </div>
              )}
              <form onSubmit={handleSendMessage} className="flex gap-2">
                <Input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Send instruction to the agent..."
                  className="text-sm"
                  data-testid="chat-input"
                  disabled={sending}
                />
                <Button
                  type="submit"
                  size="sm"
                  disabled={!chatInput.trim() || sending}
                  data-testid="chat-send-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
              <p className="text-[10px] text-muted-foreground mt-2">
                Instructions are picked up by the agent in real-time via WebSocket and applied at the next action step.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Checklist + Details */}
        <div className="space-y-6">
          {/* Checklist */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Delisting Checklist
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ChecklistRail items={job.checklist || []} />
            </CardContent>
          </Card>

          {/* Job Details */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Job Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <DetailRow icon={Globe} label="IP" value={job.ip} mono />
              <DetailRow icon={Server} label="Blacklist" value={job.blacklist} mono />
              <DetailRow icon={Mail} label="Job Email" value={job.job_email} mono />
              <Separator className="my-2" />
              <DetailRow icon={Clock} label="Created" value={formatDate(job.created_at)} />
              <DetailRow icon={Clock} label="Updated" value={formatTimeAgo(job.updated_at)} />
              {job.completed_at && (
                <DetailRow icon={Clock} label="Completed" value={formatDate(job.completed_at)} />
              )}
              {job.error && (
                <div className="mt-2 p-2 rounded bg-red-400/10 border border-red-400/20">
                  <p className="text-xs text-red-400 font-mono">{job.error}</p>
                </div>
              )}

              {/* Copy Job ID */}
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2"
                onClick={() => copyToClipboard(job.job_id)}
                data-testid="btn-copy-id"
              >
                <Copy className="w-3 h-3 mr-2" /> Copy Job ID
              </Button>
            </CardContent>
          </Card>

          {/* API Usage */}
          {usage && usage.total_calls > 0 && (
            <Card data-testid="job-usage-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                  <Zap className="w-4 h-4" /> API Usage
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Calls</p>
                    <p className="text-lg font-semibold font-mono tabular-nums" data-testid="usage-calls">{usage.total_calls}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Tokens</p>
                    <p className="text-lg font-semibold font-mono tabular-nums" data-testid="usage-tokens">{usage.total_tokens.toLocaleString()}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Cost</p>
                  <p className="text-lg font-semibold font-mono tabular-nums text-primary" data-testid="usage-cost">
                    {usage.total_cost_credits.toFixed(4)} Credits
                  </p>
                </div>
                {Object.keys(usage.by_provider || {}).length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-1.5">
                      <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wide">By Provider</p>
                      {Object.entries(usage.by_provider).map(([provider, data]) => (
                        <div key={provider} className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground capitalize">{provider}</span>
                          <span className="text-xs font-mono tabular-nums">{(typeof data === 'number' ? data : data?.tokens || 0).toLocaleString()} tok</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <span className="text-xs text-muted-foreground">{label}</span>
        <p className={`text-sm break-all ${mono ? 'font-mono' : ''}`}>{value || '—'}</p>
      </div>
    </div>
  );
}
